import axios from 'axios';
import { ApiResponse } from '../types/api';
import { API_ORIGIN } from '../config';

const API_URL = API_ORIGIN;

// API 응답 처리
const handleResponse = <T>(response: any): ApiResponse<T> => {
  if (response.data && typeof response.data.success === 'boolean') {
    return response.data as ApiResponse<T>;
  }
  
  return {
    success: response.status >= 200 && response.status < 300,
    data: response.data,
    message: '요청 성공',
    status: response.status
  };
};

// API 오류 처리
const handleError = <T>(error: any): ApiResponse<T> => {
  console.error('[Pool Monitoring API Error]', error);
  
  const errorResponse: ApiResponse<T> = {
    success: false,
    data: null,
    message: error.response?.data?.detail || error.message || '알 수 없는 오류',
    status: error.response?.status || 500
  };
  
  return errorResponse;
};

// 풀 모니터링 API 서비스
export const poolMonitoringApi = {
  // 모니터링 시작
  startMonitoring: async (poolId: number): Promise<ApiResponse<any>> => {
    try {
      console.log(`[Pool Monitoring API] 시설 ID ${poolId} 모니터링 시작 요청`);
      const response = await axios.post(`${API_URL}/api/pool-monitoring/start?pool_id=${poolId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  // 모니터링 중지
  stopMonitoring: async (poolId?: number, cctvId?: number): Promise<ApiResponse<any>> => {
    try {
      console.log(`[Pool Monitoring API] 모니터링 중지 요청 (풀ID: ${poolId}, CCTV ID: ${cctvId})`);
      let url = `${API_URL}/api/pool-monitoring/stop`;
      
      // 쿼리 파라미터 추가
      const params = new URLSearchParams();
      if (poolId) params.append('pool_id', poolId.toString());
      if (cctvId) params.append('cctv_id', cctvId.toString());
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.post(url);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  // 모니터링 상태 조회
  getMonitoringStatus: async (): Promise<ApiResponse<any>> => {
    try {
      console.log(`[Pool Monitoring API] 모니터링 상태 조회 요청`);
      const response = await axios.get(`${API_URL}/api/pool-monitoring/status`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  // 최근 감지된 위험 조회
  getRecentRisks: async (poolId?: number, limit: number = 10): Promise<ApiResponse<any>> => {
    try {
      console.log(`[Pool Monitoring API] 최근 위험 조회 요청 (풀ID: ${poolId}, 개수: ${limit})`);
      let url = `${API_URL}/api/pool-monitoring/risks/recent?limit=${limit}`;
      
      if (poolId) {
        url += `&pool_id=${poolId}`;
      }
      
      const response = await axios.get(url);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  // 웹캠으로 테스트
  testWithWebcam: async (cameraId: number = 0, poolId: number = 1, cctvId: number = 1): Promise<ApiResponse<any>> => {
    try {
      console.log(`[Pool Monitoring API] 웹캠 테스트 요청 (카메라ID: ${cameraId}, 풀ID: ${poolId}, CCTV ID: ${cctvId})`);
      const response = await axios.post(
        `${API_URL}/api/pool-monitoring/test/webcam?camera_id=${cameraId}&pool_id=${poolId}&cctv_id=${cctvId}`
      );
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  // 비디오 파일로 테스트
  testWithVideo: async (videoPath: string, poolId: number = 1, cctvId: number = 1): Promise<ApiResponse<any>> => {
    try {
      console.log(`[Pool Monitoring API] 비디오 테스트 요청 (비디오: ${videoPath}, 풀ID: ${poolId}, CCTV ID: ${cctvId})`);
      const response = await axios.post(
        `${API_URL}/api/pool-monitoring/test/video?video_path=${encodeURIComponent(videoPath)}&pool_id=${poolId}&cctv_id=${cctvId}`
      );
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  }
}; 